const KEY = 'saya.impersonation';
let switchingIdentity = false;
export function impersonationID(): string | null { return sessionStorage.getItem(KEY); }
export function clearImpersonation() { sessionStorage.removeItem(KEY); }

/** A selector bound server-side to the current administrator login, not an authentication token. */
export async function startImpersonation(targetID: number) {
  const res = await fetch('/api/admin/impersonation', {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Impersonation-Control': '1' },
    body: JSON.stringify({ target_id: targetID })
  });
  if (!res.ok) throw new Error(res.status === 404 || res.status === 503
    ? 'Impersonation is unavailable. The backend update and migration may be required.'
    : 'Unable to act as this user. Check your access and try again.');
  const data = await res.json();
  if (!data.session_id) throw new Error('The server did not return an impersonation session.');
  sessionStorage.setItem(KEY, data.session_id);
}
export async function exitImpersonation() {
  const res = await fetch('/api/impersonation/exit', {
    method: 'POST', headers: { 'X-Impersonation-Control': '1' }
  });
  if (!res.ok) throw new Error('Unable to exit impersonation. Please retry.');
  clearImpersonation();
}
export function reloadIdentity(route = '/') {
  switchingIdentity = true;
  // A full reload discards mounted components, pending renders and live telemetry.
  window.location.replace(window.location.pathname + window.location.search + '#' + route);
  window.location.reload();
}
export function installImpersonationInterceptor(onEnded: () => void): () => void {
  const original = globalThis.fetch;
  let ending = false;
  const wrapped: typeof fetch = async (input, init) => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, window.location.origin);
    const id = impersonationID();
    const isPublic = url.pathname.startsWith('/api/pay/') || url.pathname.startsWith('/api/public/');
    const local = url.origin === window.location.origin && url.pathname.startsWith('/api/');
    if ((ending || switchingIdentity) && local && !isPublic) throw new Error('Identity is changing. Please wait for the page to reload.');
    let options = init;
    let routedInput = input;
    if (id && local && !isPublic) {
      const headers = new Headers(init?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined));
      headers.set('X-Impersonation-Session', id);
      options = { ...init, headers };
      // Unknown on older API versions: fail with 404 rather than act as admin.
      if (url.pathname !== '/api/impersonation/exit') {
        url.pathname = '/api/acting/' + encodeURIComponent(id) + url.pathname.slice(4);
        routedInput = typeof Request !== 'undefined' && input instanceof Request ? new Request(url.href, input) : url.href;
      }
    }
    const response = options === undefined ? await original(routedInput) : await original(routedInput, options);
    if (id && local && !isPublic && !ending && (response.headers?.get('X-Impersonation-Ended') === 'true' || response.status === 401)) {
      ending = true;
      clearImpersonation();
      sessionStorage.setItem('saya.impersonationEnded', 'true');
      onEnded(); // Never replay a request after identity changes.
    }
    return response;
  };
  globalThis.fetch = wrapped;
  return () => { if (globalThis.fetch === wrapped) globalThis.fetch = original; };
}
